import { createContext, useContext, useState, useEffect } from 'react';

// Translations
const translations = {
  en: {
    // Navbar
    nav: {
      services: 'Services',
      portfolio: 'Portfolio',
      projects: 'Projects',
      about: 'About',
      contact: 'Contact',
      blog: 'Blog',
      chatNow: 'Chat Now',
      chatOnWhatsApp: 'Chat on WhatsApp',
    },
    // Hero
    hero: {
      badge: 'Premium Aluminium & Glass Solutions',
      title: 'Transform Your Space with',
      titleHighlight: 'Premium Glass & Aluminium',
      subtitle: 'Expert craftsmanship meets modern design. From stunning glass facades to precision aluminium works, we bring Canadian quality to UAE at competitive prices.',
      cta: 'Get Free Quote',
      trustBadges: {
        canadian: 'Canadian Expertise',
        affordable: 'Affordable Pricing',
        professional: 'Professional Team',
      },
    },
    // Services
    services: {
      title: 'Our Services',
      subtitle: 'Comprehensive solutions for all your aluminium, glass, and maintenance needs',
      aluminium: {
        title: 'Aluminium Works',
        desc: 'Premium windows, doors, partitions & facades',
      },
      glass: {
        title: 'Glass Works',
        desc: 'Frameless glass, office partitions & shopfronts',
      },
      drywall: {
        title: 'Drywall Installation',
        desc: 'Professional gypsum board & ceiling works',
      },
      flooring: {
        title: 'Flooring Services',
        desc: 'Tiles, vinyl, laminate & epoxy flooring',
      },
      painting: {
        title: 'Painting Services',
        desc: 'Interior & exterior professional painting',
      },
      cleaning: {
        title: 'Cleaning Services',
        desc: 'Deep cleaning & maintenance services',
      },
    },
    // Portfolio
    portfolio: {
      title: 'Our Portfolio',
      subtitle: 'View our recent aluminium and glass installation projects across UAE',
    },
    // Projects
    projects: {
      title: 'Featured Projects',
      subtitle: 'Explore our diverse portfolio of completed works',
      viewProject: 'View Project',
      filterAll: 'All',
      filterAluminium: 'Aluminium',
      filterGlass: 'Glass',
      filterMaintenance: 'Maintenance',
    },
    // About
    about: {
      title: 'About Us',
      subtitle: 'Your Trusted Partner in UAE',
      description: 'With years of experience in the UAE market, Glow Space LLC has established itself as a leading provider of premium aluminium and glass solutions. Our commitment to quality, backed by Canadian expertise, ensures every project meets the highest standards.',
      stats: {
        projects: 'Projects Completed',
        clients: 'Happy Clients',
        years: 'Years Experience',
      },
      highlights: {
        quality: {
          title: 'Quality Materials',
          desc: 'Premium grade aluminium and glass',
        },
        skilled: {
          title: 'Skilled Craftsmen',
          desc: 'Experienced professionals',
        },
        timely: {
          title: 'Timely Delivery',
          desc: 'On-schedule completion',
        },
        support: {
          title: '24/7 Support',
          desc: 'Always here to help',
        },
      },
    },
    // Why Choose Us
    whyChooseUs: {
      title: 'Why Choose Us',
      subtitle: 'What sets Glow Space apart',
      reasons: {
        expertise: {
          title: 'Canadian Expertise',
          desc: 'Bringing international standards and innovative techniques to every project.',
        },
        pricing: {
          title: 'Competitive Pricing',
          desc: 'Premium quality at prices that respect your budget.',
        },
        quality: {
          title: 'Quality Assured',
          desc: 'Every project backed by warranty and quality guarantee.',
        },
        support: {
          title: 'Full Support',
          desc: 'From consultation to completion, we\'re with you every step.',
        },
      },
      cta: 'Start Your Project',
    },
    // Contact
    contact: {
      title: 'Contact Us',
      subtitle: 'Get in touch for a free consultation',
      info: {
        phone: 'Phone',
        email: 'Email',
        address: 'Address',
      },
      form: {
        name: 'Your Name',
        email: 'Email Address',
        service: 'Service Needed',
        selectService: 'Select a service',
        message: 'Your Message',
        messagePlaceholder: 'Tell us about your project...',
        submit: 'Send via WhatsApp',
      },
    },
    // Testimonials
    testimonials: {
      title: 'What Our Clients Say',
      subtitle: 'Trusted by homeowners and businesses across UAE',
    },
    // Blog
    blog: {
      title: 'Latest News & Insights',
      subtitle: 'Tips, trends, and updates from the aluminium & glass industry',
      readMore: 'Read More',
      backToBlog: 'Back to Blog',
      recentPosts: 'Recent Posts',
      categories: 'Categories',
      allPosts: 'All Posts',
    },
    // Footer
    footer: {
      description: 'Premium aluminium and glass solutions in the UAE. Original Canadian quality at competitive prices.',
      services: 'Our Services',
      quickLinks: 'Quick Links',
      getQuote: 'Get a Free Quote',
      quoteDesc: 'Ready to start your project? Contact us on WhatsApp for a quick response and free consultation.',
      copyright: '© {year} Glow Space LLC. All rights reserved.',
    },
    // Common
    common: {
      learnMore: 'Learn More',
      viewAll: 'View All',
      close: 'Close',
    },
  },
  ar: {
    // Navbar
    nav: {
      services: 'الخدمات',
      portfolio: 'أعمالنا',
      projects: 'المشاريع',
      about: 'من نحن',
      contact: 'اتصل بنا',
      blog: 'المدونة',
      chatNow: 'تحدث الآن',
      chatOnWhatsApp: 'تحدث عبر واتساب',
    },
    // Hero
    hero: {
      badge: 'حلول الألمنيوم والزجاج الفاخرة',
      title: 'حوّل مساحتك مع',
      titleHighlight: 'زجاج وألمنيوم فاخر',
      subtitle: 'حرفية متميزة تلتقي بالتصميم الحديث. من واجهات الزجاج المذهلة إلى أعمال الألمنيوم الدقيقة، نقدم جودة كندية في الإمارات بأسعار تنافسية.',
      cta: 'احصل على عرض سعر مجاني',
      trustBadges: {
        canadian: 'خبرة كندية',
        affordable: 'أسعار معقولة',
        professional: 'فريق محترف',
      },
    },
    // Services
    services: {
      title: 'خدماتنا',
      subtitle: 'حلول شاملة لجميع احتياجاتك من الألمنيوم والزجاج والصيانة',
      aluminium: {
        title: 'أعمال الألمنيوم',
        desc: 'نوافذ وأبواب وفواصل وواجهات فاخرة',
      },
      glass: {
        title: 'أعمال الزجاج',
        desc: 'زجاج بدون إطار، فواصل مكتبية وواجهات محلات',
      },
      drywall: {
        title: 'تركيب الجبس بورد',
        desc: 'أعمال ألواح الجبس والأسقف المحترفة',
      },
      flooring: {
        title: 'خدمات الأرضيات',
        desc: 'بلاط، فينيل، لامينيت وأرضيات إيبوكسي',
      },
      painting: {
        title: 'خدمات الدهان',
        desc: 'دهان داخلي وخارجي محترف',
      },
      cleaning: {
        title: 'خدمات التنظيف',
        desc: 'تنظيف عميق وخدمات صيانة',
      },
    },
    // Portfolio
    portfolio: {
      title: 'أعمالنا',
      subtitle: 'شاهد مشاريع تركيب الألمنيوم والزجاج الأخيرة في جميع أنحاء الإمارات',
    },
    // Projects
    projects: {
      title: 'المشاريع المميزة',
      subtitle: 'استكشف محفظتنا المتنوعة من الأعمال المنجزة',
      viewProject: 'عرض المشروع',
      filterAll: 'الكل',
      filterAluminium: 'ألمنيوم',
      filterGlass: 'زجاج',
      filterMaintenance: 'صيانة',
    },
    // About
    about: {
      title: 'من نحن',
      subtitle: 'شريكك الموثوق في الإمارات',
      description: 'مع سنوات من الخبرة في سوق الإمارات، رسّخت جلو سبيس ذاتها كمزود رائد لحلول الألمنيوم والزجاج الفاخرة. التزامنا بالجودة، المدعوم بالخبرة الكندية، يضمن أن كل مشروع يلبي أعلى المعايير.',
      stats: {
        projects: 'مشروع مكتمل',
        clients: 'عميل سعيد',
        years: 'سنوات خبرة',
      },
      highlights: {
        quality: {
          title: 'مواد عالية الجودة',
          desc: 'ألمنيوم وزجاج من الدرجة الممتازة',
        },
        skilled: {
          title: 'حرفيون ماهرون',
          desc: 'محترفون ذوو خبرة',
        },
        timely: {
          title: 'تسليم في الوقت المحدد',
          desc: 'إنجاز حسب الجدول الزمني',
        },
        support: {
          title: 'دعم على مدار الساعة',
          desc: 'دائماً هنا للمساعدة',
        },
      },
    },
    // Why Choose Us
    whyChooseUs: {
      title: 'لماذا تختارنا',
      subtitle: 'ما يميز جلو سبيس',
      reasons: {
        expertise: {
          title: 'خبرة كندية',
          desc: 'نقدم معايير دولية وتقنيات مبتكرة لكل مشروع.',
        },
        pricing: {
          title: 'أسعار تنافسية',
          desc: 'جودة ممتازة بأسعار تحترم ميزانيتك.',
        },
        quality: {
          title: 'جودة مضمونة',
          desc: 'كل مشروع مدعوم بضمان وكفالة الجودة.',
        },
        support: {
          title: 'دعم كامل',
          desc: 'من الاستشارة إلى الإنجاز، نحن معك في كل خطوة.',
        },
      },
      cta: 'ابدأ مشروعك',
    },
    // Contact
    contact: {
      title: 'اتصل بنا',
      subtitle: 'تواصل معنا للحصول على استشارة مجانية',
      info: {
        phone: 'الهاتف',
        email: 'البريد الإلكتروني',
        address: 'العنوان',
      },
      form: {
        name: 'اسمك',
        email: 'البريد الإلكتروني',
        service: 'الخدمة المطلوبة',
        selectService: 'اختر خدمة',
        message: 'رسالتك',
        messagePlaceholder: 'أخبرنا عن مشروعك...',
        submit: 'أرسل عبر واتساب',
      },
    },
    // Testimonials
    testimonials: {
      title: 'ماذا يقول عملاؤنا',
      subtitle: 'موثوق به من قبل أصحاب المنازل والشركات في جميع أنحاء الإمارات',
    },
    // Blog
    blog: {
      title: 'آخر الأخبار والمقالات',
      subtitle: 'نصائح واتجاهات وتحديثات من صناعة الألمنيوم والزجاج',
      readMore: 'اقرأ المزيد',
      backToBlog: 'العودة للمدونة',
      recentPosts: 'المقالات الأخيرة',
      categories: 'الفئات',
      allPosts: 'جميع المقالات',
    },
    // Footer
    footer: {
      description: 'حلول الألمنيوم والزجاج الفاخرة في الإمارات. جودة كندية أصلية بأسعار تنافسية.',
      services: 'خدماتنا',
      quickLinks: 'روابط سريعة',
      getQuote: 'احصل على عرض سعر مجاني',
      quoteDesc: 'مستعد لبدء مشروعك؟ تواصل معنا عبر واتساب للحصول على رد سريع واستشارة مجانية.',
      copyright: '© {year} جلو سبيس ذ.م.م. جميع الحقوق محفوظة.',
    },
    // Common
    common: {
      learnMore: 'اعرف المزيد',
      viewAll: 'عرض الكل',
      close: 'إغلاق',
    },
  },
};

// Language Context
const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  const isRTL = language === 'ar';

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    
    // Update font family for Arabic
    if (isRTL) {
      document.body.style.fontFamily = "'Noto Sans Arabic', 'Manrope', sans-serif";
    } else {
      document.body.style.fontFamily = "'Manrope', sans-serif";
    }
  }, [language, isRTL]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, isRTL, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default translations;
